import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, FileText, Image as ImageIcon, Lock, PlayCircle, ShieldAlert } from "lucide-react";

import { fetchLearnerContentFileBlobApi } from "../../../../services/learnerModulesApi";
import ArticleContentViewer from "./ArticleContentViewer";
import CertificateContent from "./CertificateContent";
import "./ContentRenderer.css";
import DocumentContentViewer from "./DocumentContentViewer";
import EventContentCard from "./EventContentCard";
import ImageContentViewer from "./ImageContentViewer";
import LinkContentViewer from "./LinkContentViewer";
import PdfContentViewer from "./PdfContentViewer";
import EvaluationRunner from "./EvaluationRunner";
import QuizRunner from "./QuizRunner";
import ScormPlayer from "./ScormPlayer";
import VideoContentPlayer from "./VideoContentPlayer";

const FILE_BACKED_TYPES = new Set(["PDF", "WORD", "EXCEL", "PPT", "VIDEO", "IMAGE"]);

const formatSize = (value) => {
    if (!value && value !== 0) return null;
    if (value < 1024) return `${value} o`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} Ko`;
    return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
};

const renderPlaceholder = (title, description) => (
    <div className="cr-placeholder">
        <ShieldAlert size={22} />
        <div>
            <strong>{title}</strong>
            <p>{description}</p>
        </div>
    </div>
);

const getSaveStatusMessage = (saveStatus, saveError) => {
    switch (saveStatus) {
        case "saving":
            return "Sauvegarde...";
        case "saved":
            return "Progression sauvegardée";
        case "offline":
            return "Hors ligne, nouvelle tentative au prochain envoi.";
        case "error":
            return saveError || "Echec de sauvegarde.";
        default:
            return null;
    }
};

const WORKFLOW_MANAGED_TYPES = new Set(["SCORM", "QUIZ", "EVALUATION", "CERTIFICAT", "EVENEMENT"]);

export default function ContentRenderer({
    moduleItem,
    content,
    onComplete,
    onTrack,
    onRefreshPlayer,
    scormNodeRequest,
    navigationRequest,
    onScormNavigationChange,
    onContextChange,
    saveStatus,
    saveError,
    hideHeader = false,
}) {
    const progress = content.progress || {};
    const isCompleted = String(progress.status || "").toUpperCase() === "COMPLETED";
    const isWorkflowManaged = WORKFLOW_MANAGED_TYPES.has(content.contentType);
    const showManualComplete = !isWorkflowManaged && progress.manualCompletionRequired !== false;
    const [fileUrl, setFileUrl] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);

    const isFileBacked = useMemo(() => FILE_BACKED_TYPES.has(content.contentType), [content.contentType]);

    useEffect(() => {
        if (!isFileBacked || !content.moduleId || !content.id) {
            setFileUrl(null);
            setFileError(null);
            setFileLoading(false);
            return undefined;
        }

        let active = true;
        let objectUrl = null;

        const loadFile = async () => {
            setFileLoading(true);
            setFileError(null);
            try {
                const response = await fetchLearnerContentFileBlobApi(content.moduleId, content.id);
                objectUrl = URL.createObjectURL(response.data);
                if (active) {
                    setFileUrl(objectUrl);
                }
            } catch (error) {
                if (active) {
                    setFileUrl(null);
                    setFileError("Le fichier n'est pas accessible pour ce contenu.");
                }
            } finally {
                if (active) {
                    setFileLoading(false);
                }
            }
        };

        loadFile();

        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [content.id, content.moduleId, content.contentType, isFileBacked]);

    const renderContentBody = () => {
        switch (content.contentType) {
            case "VIDEO":
                if (fileLoading) return renderPlaceholder("Chargement video", "Le media est en cours de preparation.");
                return fileUrl ? <VideoContentPlayer content={content} fileUrl={fileUrl} progress={progress} onTrack={onTrack} onContextChange={onContextChange} navigationRequest={navigationRequest} /> : renderPlaceholder("Video indisponible", fileError || "Aucune source video n'est disponible pour ce contenu.");

            case "PDF":
                if (fileLoading) return renderPlaceholder("Chargement PDF", "Le document PDF est en cours de chargement.");
                return fileUrl ? <PdfContentViewer key={`${content.id}-${fileUrl}`} content={content} fileUrl={fileUrl} progress={progress} onTrack={onTrack} onContextChange={onContextChange} navigationRequest={navigationRequest} /> : renderPlaceholder("PDF indisponible", fileError || "Le document PDF n'est pas accessible pour le moment.");

            case "ARTICLE":
                return <ArticleContentViewer content={content} progress={progress} onTrack={onTrack} onContextChange={onContextChange} navigationRequest={navigationRequest} />;

            case "IMAGE":
                if (fileLoading) return renderPlaceholder("Chargement image", "L'image est en cours de chargement.");
                return fileUrl ? <ImageContentViewer content={content} fileUrl={fileUrl} onTrack={onTrack} /> : renderPlaceholder("Image indisponible", fileError || "Aucune image n'est disponible pour ce contenu.");

            case "LIEN_EXTERNE":
                return content.externalUrl
                    ? <LinkContentViewer content={content} progress={progress} onTrack={onTrack} />
                    : renderPlaceholder("Lien externe indisponible", "Aucune URL n'a ete configuree.");

            case "WORD":
            case "EXCEL":
            case "PPT":
                if (fileLoading) return renderPlaceholder("Chargement document", "Le document est en cours de chargement.");
                return fileUrl
                    ? <DocumentContentViewer content={content} fileUrl={fileUrl} progress={progress} onTrack={onTrack} />
                    : renderPlaceholder("Document indisponible", fileError || "Le fichier n'est pas encore accessible.");

            case "SCORM":
                return (
                    <ScormPlayer
                        moduleId={content.moduleId || moduleItem?.id}
                        content={content}
                        nodeRequest={scormNodeRequest}
                        onNavigationChange={onScormNavigationChange}
                        onContextChange={onContextChange}
                    />
                );

            case "QUIZ":
                return (
                    <QuizRunner
                        key={`${content.id}-${content.quizId || content.quizLabel || "quiz"}`}
                        moduleId={content.moduleId}
                        content={content}
                        onRefreshPlayer={onRefreshPlayer}
                    />
                );

            case "EVALUATION":
                return (
                    <EvaluationRunner
                        key={`${content.id}-${content.evaluationId || content.evaluationLabel || "evaluation"}`}
                        moduleId={content.moduleId}
                        content={content}
                        onRefreshPlayer={onRefreshPlayer}
                    />
                );

            case "CERTIFICAT":
                return (
                    <CertificateContent
                        key={`${content.id}-${content.certificatId || content.title || "certificate"}`}
                        moduleId={content.moduleId}
                        content={content}
                        onRefreshPlayer={onRefreshPlayer}
                    />
                );

            case "EVENEMENT":
                return (
                    <EventContentCard
                        key={`${content.id}-${content.evenementId || content.evenementLabel || "event"}`}
                        moduleId={content.moduleId}
                        content={content}
                        onRefreshPlayer={onRefreshPlayer}
                    />
                );

            default:
                return renderPlaceholder("Contenu non pris en charge", "Ce format sera complete dans un lot ulterieur.");
        }
    };

    return (
        <section className={`cr-panel ${hideHeader ? "cr-panel--no-header" : ""}`}>
            {!hideHeader && (
                <>
                    <div className="cr-topbar">
                        <div>
                            <span className="cr-module-name">{moduleItem?.nom}</span>
                            <h2>{content.title}</h2>
                            <p>{content.description || "Contenu de formation accessible depuis votre player apprenant."}</p>
                        </div>
                        <div className="cr-status-box">
                            <span className={`cr-status ${content.locked ? "cr-status--locked" : isCompleted ? "cr-status--done" : "cr-status--progress"}`}>
                                {content.locked ? <Lock size={14} /> : isCompleted ? <CheckCircle2 size={14} /> : <PlayCircle size={14} />}
                                {content.locked ? "Verrouillé" : isCompleted ? "Terminé" : "En cours"}
                            </span>
                            <span className="cr-progress-number">{progress.progressPercent || 0}%</span>
                        </div>
                    </div>

                    <div className="cr-meta">
                        <span>
                            <FileText size={14} />
                            {content.contentType}
                        </span>
                        {content.fileName ? <span>{content.fileName}</span> : null}
                        {content.fileSize ? <span>{formatSize(content.fileSize)}</span> : null}
                        {progress.timeSpentSeconds ? (
                            <span>
                                <Clock3 size={14} />
                                {progress.timeSpentSeconds}s
                            </span>
                        ) : null}
                        {content.contentType === "IMAGE" ? (
                            <span>
                                <ImageIcon size={14} />
                                Media
                            </span>
                        ) : null}
                    </div>
                </>
            )}

            {content.locked ? (
                <div className="cr-locked">
                    <Lock size={18} />
                    <div>
                        <strong>Ce contenu est verrouille.</strong>
                        <p>{content.lockedReason || "Le backend signale que ce contenu n'est pas encore accessible."}</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="cr-body">{renderContentBody()}</div>

                    <div className="cr-actions">
                        {showManualComplete ? (
                            <button className="cr-btn cr-btn--primary" onClick={onComplete} disabled={isCompleted}>
                                <CheckCircle2 size={16} />
                                {isCompleted ? "Contenu terminé" : "Marquer comme terminé"}
                            </button>
                        ) : null}
                        {/* SCORM save status message hidden as per user request */}
                        {getSaveStatusMessage(saveStatus, saveError) ? (
                            <div className={`cr-save-status cr-save-status--${saveStatus || "idle"}`}>
                                {getSaveStatusMessage(saveStatus, saveError)}
                            </div>
                        ) : null}
                    </div>
                </>
            )}
        </section>
    );
}
